package com.schoolconnect.app.feature.notices

import androidx.compose.foundation.background
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.Image
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Surface
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntRect
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.window.Popup
import androidx.compose.ui.window.PopupPositionProvider
import androidx.compose.ui.window.PopupProperties
import com.schoolconnect.app.designsystem.*
import com.schoolconnect.app.repository.files.FileUploadRepository
import com.schoolconnect.app.util.resizeImage
import com.schoolconnect.app.util.decodeImageBitmap
import io.github.vinceglb.filekit.compose.rememberFilePickerLauncher
import io.github.vinceglb.filekit.core.PickerMode
import io.github.vinceglb.filekit.core.PickerType
import io.github.vinceglb.filekit.core.PlatformFile
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.cloud_upload
import ischoolconnect.sharedui.generated.resources.chevron_down
import ischoolconnect.sharedui.generated.resources.close_x
import ischoolconnect.sharedui.generated.resources.document
import ischoolconnect.sharedui.generated.resources.eye
import ischoolconnect.sharedui.generated.resources.megaphone
import ischoolconnect.sharedui.generated.resources.search
import org.jetbrains.compose.resources.painterResource
import kotlinx.coroutines.launch

private data class NoticeAttachmentUi(
    val file: PlatformFile?,
    val name: String,
    val type: String,
    val sizeLabel: String,
    val fileId: String? = null,
    val previewBytes: ByteArray? = null,
    val url: String = ""
)

@Composable
internal fun AddNoticeDialog(
    visible: Boolean,
    initialNotice: NoticeRowUi? = null,
    initialDetail: NoticeDetailUi? = null,
    audienceOptions: List<NoticeAudienceOption> = emptyList(),
    classTargetOptions: List<NoticeClassTargetOption> = emptyList(),
    isLoadingDetails: Boolean = false,
    isSaving: Boolean = false,
    errorMessage: String = "",
    onDismiss: () -> Unit,
    onSave: (NoticeDraft) -> Unit = {}
) {
    if (!visible) return
    key(initialNotice?.id ?: "new") {
        var title by remember { mutableStateOf(initialDetail?.title ?: initialNotice?.title.orEmpty()) }
        var audience by remember { mutableStateOf(initialDetail?.audience ?: initialNotice?.audience ?: "All") }
        var classTargetIds by remember { mutableStateOf(initialDetail?.classTargetIds.orEmpty()) }
        LaunchedEffect(audienceOptions, initialDetail?.audience, initialNotice?.audience) {
            val current = audience.trim()
            audienceOptions.firstOrNull { option ->
                option.code.equals(current, ignoreCase = true) || option.label.equals(current, ignoreCase = true)
            }?.let { audience = it.label }
        }
        var content by remember { mutableStateOf((initialDetail?.content ?: if (initialNotice?.status == "Draft") "Dear Parents,\n\nPlease review the details shared in this notice.\n\nRegards,\nSchool Administration" else "").take(2000)) }
        var expiry by remember { mutableStateOf(initialDetail?.expiryDate ?: initialNotice?.expiryDate.orEmpty()) }
        var hydratedDetailKey by remember { mutableStateOf("") }
        var isUploadingFiles by remember { mutableStateOf(false) }
        var previewVisible by remember { mutableStateOf(false) }
        var attachmentError by remember { mutableStateOf("") }
        var hasSubmitted by remember { mutableStateOf(false) }
        val minimumExpiryDate = currentAppDateLabel()
        val titleError = if (hasSubmitted && title.isBlank()) "Notice title is required." else ""
        val audienceError = if (hasSubmitted && audience.isBlank()) "Audience is required." else ""
        val selectedAudienceCode = audienceOptions.firstOrNull { it.label.equals(audience, ignoreCase = true) || it.code.equals(audience, ignoreCase = true) }?.code?.uppercase() ?: audience.trim().uppercase()
        val classTargetError = if (hasSubmitted && selectedAudienceCode == "CLASS" && classTargetIds.isEmpty()) "Select at least one class." else ""
        val contentError = if (hasSubmitted && content.isBlank()) "Notice content is required." else ""
        val expiryError = when {
            !hasSubmitted || expiry.isBlank() -> ""
            appDateInputComparable(expiry) == null -> "Enter a valid expiry date."
            appDateInputComparable(expiry)!! < appDateInputComparable(minimumExpiryDate)!! -> "Expiry date cannot be earlier than today."
            else -> ""
        }
        var uploadSession by remember {
            mutableStateOf(
                FileUploadRepository.newSession("NOTICE").copy(
                    recordId = initialNotice?.backendId?.takeIf { it.isNotBlank() },
                    uploadedFileIds = initialNotice?.attachmentFileIds.orEmpty()
                )
            )
        }
        val coroutineScope = rememberCoroutineScope()
        var attachments by remember {
            mutableStateOf(
                when {
                    initialDetail?.attachments?.isNotEmpty() == true -> initialDetail.attachments.map { attachment ->
                        NoticeAttachmentUi(null, attachment.name, attachment.mimeType.substringAfterLast('/', "FILE").uppercase(), attachment.sizeLabel, attachment.fileId, attachment.previewBytes, attachment.url)
                    }
                    initialNotice != null -> initialNotice.attachmentFileIds.mapIndexed { index, fileId ->
                        NoticeAttachmentUi(null, "Attachment ${index + 1}", "FILE", "Existing attachment", fileId, url = initialNotice.attachmentFileUrls.getOrNull(index).orEmpty())
                    }
                    else -> emptyList()
                }
            )
        }
        LaunchedEffect(initialDetail?.attachments) {
            if (initialDetail?.attachments?.isNotEmpty() == true && attachments.all { it.file == null }) {
                attachments = initialDetail.attachments.map { attachment ->
                    NoticeAttachmentUi(null, attachment.name, attachment.mimeType.substringAfterLast('/', "FILE").uppercase(), attachment.sizeLabel, attachment.fileId, attachment.previewBytes, attachment.url)
                }
            }
        }
        LaunchedEffect(initialDetail?.id, initialDetail?.updatedAt) {
            val detail = initialDetail ?: return@LaunchedEffect
            val detailKey = "${detail.id}:${detail.updatedAt}"
            if (detailKey != hydratedDetailKey) {
                title = detail.title
                audience = detail.audience
                classTargetIds = detail.classTargetIds
                content = detail.content.take(2000)
                expiry = detail.expiryDate.takeUnless { it == "—" }.orEmpty()
                hydratedDetailKey = detailKey
            }
        }
        val attachmentPicker = rememberFilePickerLauncher(
            type = PickerType.File(listOf("pdf", "docx", "jpg", "jpeg", "png")),
            mode = PickerMode.Multiple(),
            title = "Select Notice Attachments"
        ) { files ->
            if (files != null) coroutineScope.launch {
                isUploadingFiles = true
                attachmentError = ""
                for (file in files) {
                    if (attachments.any { it.name.equals(file.name, ignoreCase = true) }) continue
                    val bytes = runCatching { file.readBytes() }.getOrNull()
                    if (bytes == null || bytes.isEmpty()) {
                        attachmentError = "Unable to read ${file.name}."
                        continue
                    }
                    val isImage = file.name.substringAfterLast('.', "").lowercase() in setOf("jpg", "jpeg", "png", "webp")
                    val uploadBytes = if (isImage) resizeImage(bytes) else bytes
                    val uploadFilename = if (isImage && uploadBytes !== bytes) {
                        "${file.name.substringBeforeLast('.', file.name)}.jpg"
                    } else {
                        file.name
                    }
                    FileUploadRepository.upload(
                        session = uploadSession,
                        filename = uploadFilename,
                        contentType = noticeContentType(uploadFilename),
                        bytes = uploadBytes
                    ).fold(
                        onSuccess = { uploaded ->
                            uploadSession = uploaded.first
                            attachments = attachments + NoticeAttachmentUi(
                                file = file,
                                name = file.name,
                                type = file.name.substringAfterLast('.', "FILE").uppercase(),
                                sizeLabel = formatAttachmentSize(file.getSize()),
                                fileId = uploaded.second.fileId,
                                previewBytes = if (isImage) uploadBytes else null
                            )
                        },
                        onFailure = { throwable -> attachmentError = throwable.message ?: "Unable to upload ${file.name}." }
                    )
                }
                isUploadingFiles = false
            }
        }

        AppDialogScaffold(
            onDismiss = onDismiss,
            size = AppDialogSize.Wide,
            maxDialogHeight = 700.dp,
            fillDialogHeight = true,
            isLoading = isUploadingFiles || isSaving || isLoadingDetails,
            loadingTitle = when {
                isUploadingFiles -> "Uploading attachments"
                isSaving -> "Saving notice"
                else -> "Loading notice details"
            }
        ) {
            AppDialogHeader(
                title = when {
                    initialNotice == null -> "Add Notice"
                    initialNotice.status == "Draft" -> "Edit Draft Notice"
                    else -> "Edit Notice"
                },
                subtitle = if (initialNotice == null) "Create a new notice for parents and school communication." else "Update notice details before publishing.",
                titleSuffix = initialNotice?.id,
                onClose = onDismiss,
                icon = Res.drawable.megaphone,
                iconContentDescription = "Notice",
                colors = AppDialogDefaultColors
            )
            AppDialogValidationSummary(
                errors = listOf(titleError, audienceError, classTargetError, contentError, expiryError),
                modifier = Modifier.padding(horizontal = 28.dp, vertical = 12.dp)
            )
            AppDialogErrorMessage(
                message = errorMessage,
                modifier = Modifier.padding(horizontal = 28.dp, vertical = 4.dp)
            )
            Column(
                Modifier
                    .fillMaxWidth()
                    .weight(1f)
                    .verticalScroll(rememberScrollState())
                    .padding(horizontal = 28.dp, vertical = 12.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                NoticeTitle(title, titleError) { title = it }
                NoticeAudienceDropdown(
                    value = audienceOptions.firstOrNull { it.label.equals(audience, ignoreCase = true) || it.code.equals(audience, ignoreCase = true) }?.label ?: audience,
                    options = audienceOptions,
                    error = audienceError,
                    onSelected = { audience = it.label }
                )
                if (selectedAudienceCode == "CLASS") {
                    val selectedLabels = classTargetIds.mapNotNull { id -> classTargetOptions.firstOrNull { it.id == id }?.label }
                    NoticeMultiSelectField(
                        label = "Classes",
                        values = selectedLabels,
                        options = classTargetOptions.map { it.label },
                        modifier = Modifier.fillMaxWidth(),
                        onValuesChanged = { labels ->
                            classTargetIds = labels.mapNotNull { label -> classTargetOptions.firstOrNull { it.label == label }?.id }
                        }
                    )
                    if (classTargetOptions.isEmpty()) {
                        Text("No active class sections are available.", color = AppMuted, fontSize = 12.sp)
                    }
                    if (classTargetError.isNotBlank()) Text(classTargetError, color = AppDanger, fontSize = 12.sp)
                }
                NoticeRichContent(content, contentError) { content = it }
                AppDialogFieldColumn(label = "Expiry Date", helperText = "Optional", error = expiryError, colors = AppDialogDefaultColors, modifier = Modifier.widthIn(max = if (initialNotice == null) 560.dp else 360.dp)) {
                    AppDialogDateField(expiry, { expiry = it }, placeholder = "DD MMM YYYY", colors = AppDialogDefaultColors, fontSize = 14.sp, error = expiryError, minimumDate = minimumExpiryDate)
                }
                AppDialogFieldColumn(label = "Attachments", helperText = "Optional", colors = AppDialogDefaultColors) {
                    Row(Modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(8.dp)).padding(10.dp), verticalAlignment = Alignment.CenterVertically) {
                        AppOutlinedButton(onClick = { attachmentPicker.launch() }, modifier = Modifier.width(170.dp).height(44.dp)) {
                            Icon(painterResource(Res.drawable.cloud_upload), null, tint = AppPrimary, modifier = Modifier.size(18.dp))
                            Spacer(Modifier.width(7.dp)); Text("Upload Files", color = AppPrimary, fontWeight = FontWeight.SemiBold)
                        }
                        Spacer(Modifier.width(14.dp)); Text(if (attachments.isEmpty()) "No files selected" else "${attachments.size} file${if (attachments.size == 1) "" else "s"} selected", color = AppMuted, fontSize = 13.sp)
                    }
                    if (attachments.isNotEmpty()) {
                        Spacer(Modifier.height(8.dp))
                        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                            attachments.chunked(2).forEach { row ->
                                Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                                    row.forEach { attachment ->
                                        NoticeAttachmentChip(attachment, Modifier.weight(1f)) { removed ->
                                            attachments = attachments - removed
                                            removed.fileId
                                                ?.takeIf { it !in initialNotice?.attachmentFileIds.orEmpty() }
                                                ?.let { fileId -> coroutineScope.launch { FileUploadRepository.delete(fileId) } }
                                        }
                                    }
                                    if (row.size == 1) Spacer(Modifier.weight(1f))
                                }
                            }
                        }
                    }
                    Text("Supported: PDF, DOCX, JPG, PNG (Max 10 MB per file)", color = AppMuted, fontSize = 12.sp)
                }
                if (attachmentError.isNotBlank()) {
                    Text(attachmentError, color = AppDanger, fontSize = 12.sp)
                }
            }
            NoticeDialogFooter(
                onDismiss = onDismiss,
                enabled = !isSaving && !isUploadingFiles,
                allowSaveAsDraft = initialNotice == null || initialNotice.status == "Draft",
                onPreview = { previewVisible = true },
                onSave = { isActive ->
                    hasSubmitted = true
                    if (listOf(titleError, audienceError, classTargetError, contentError, expiryError).all { it.isBlank() }) {
                        onSave(NoticeDraft(title.trim(), content.trim(), audience.trim(), expiry, isActive, attachments.mapNotNull { it.fileId }, classTargetIds, initialNotice?.status))
                    }
                }
            )
        }

        val previewNotice = NoticeRowUi(
            id = "Preview",
            backendId = "",
            title = title.ifBlank { "Untitled notice" },
            audience = audience.ifBlank { "—" },
            publishedOn = "—",
            expiryDate = expiry.ifBlank { "—" },
            status = if (initialNotice?.status == "Draft") "Draft" else "Active",
            hasAttachments = attachments.isNotEmpty(),
            attachmentFileIds = attachments.mapNotNull { it.fileId }
        )
        val previewDetail = NoticeDetailUi(
            id = "Preview",
            title = title.ifBlank { "Untitled notice" },
            content = content,
            audience = audience.ifBlank { "—" },
            expiryDate = expiry.ifBlank { "—" },
            status = previewNotice.status,
            createdAt = "—",
            updatedAt = "—",
            attachments = attachments.mapIndexed { index, attachment ->
                NoticeAttachmentDetailUi(
                    fileId = attachment.fileId.orEmpty(),
                    name = attachment.name.ifBlank { "Attachment ${index + 1}" },
                    mimeType = noticeContentType("file.${attachment.type.lowercase()}"),
                    sizeLabel = attachment.sizeLabel,
                    previewBytes = attachment.previewBytes,
                    url = attachment.url
                )
            }
        )
        ViewNoticeDialog(
            notice = if (previewVisible) previewNotice else null,
            detail = previewDetail,
            isLoading = false,
            errorMessage = "",
            onDismiss = { previewVisible = false },
            onView = {},
            loadDetail = false
        )
    }
}

@Composable
private fun NoticeAudienceDropdown(
    value: String,
    options: List<NoticeAudienceOption>,
    error: String,
    onSelected: (NoticeAudienceOption) -> Unit
) {
    var expanded by remember { mutableStateOf(false) }
    var anchorWidthPx by remember { mutableStateOf(0) }
    val anchorWidth = with(LocalDensity.current) { anchorWidthPx.toDp() }
    AppDialogFieldColumn(label = "Audience", required = true, error = error, colors = AppDialogDefaultColors) {
        Box {
            Row(
                Modifier
                    .fillMaxWidth()
                    .height(40.dp)
                    .border(1.dp, if (expanded) AppPrimary else AppBorder, RoundedCornerShape(6.dp))
                    .clickable(enabled = options.isNotEmpty()) { expanded = !expanded }
                    .onGloballyPositioned { anchorWidthPx = it.size.width }
                    .padding(horizontal = 12.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = value.ifBlank { if (options.isEmpty()) "Loading audiences…" else "Select audience" },
                    color = if (value.isBlank()) AppMuted else AppNavy,
                    fontSize = 14.sp,
                    modifier = Modifier.weight(1f)
                )
                Icon(
                    painterResource(Res.drawable.chevron_down),
                    contentDescription = "Select audience",
                    tint = if (options.isEmpty()) AppMuted else AppNavy,
                    modifier = Modifier.size(18.dp)
                )
            }
            AppDropdownMenu(
                expanded = expanded,
                onDismissRequest = { expanded = false },
                width = anchorWidth.takeIf { it > 0.dp },
                containerColor = Color.White,
                border = BorderStroke(1.dp, AppBorder)
            ) {
                options.forEach { option ->
                    DropdownMenuItem(
                        text = { Text(option.label, color = AppNavy, fontSize = 14.sp) },
                        onClick = { expanded = false; onSelected(option) }
                    )
                }
            }
        }
    }
}

@Composable
private fun NoticeMultiSelectField(label: String, values: List<String>, options: List<String>, modifier: Modifier, onValuesChanged: (List<String>) -> Unit) {
    var expanded by remember { mutableStateOf(false) }
    var query by remember { mutableStateOf("") }
    var visibleCount by remember { mutableStateOf(5) }
    var anchorWidthPx by remember { mutableStateOf(0) }
    val listState = rememberLazyListState()
    val chipScrollState = rememberScrollState()
    val anchorWidth = with(LocalDensity.current) { anchorWidthPx.toDp() }
    val filteredOptions = remember(query, options) { options.filter { it.contains(query.trim(), ignoreCase = true) } }
    LaunchedEffect(query, expanded) {
        visibleCount = 5
        listState.scrollToItem(0)
    }
    LaunchedEffect(listState, expanded, filteredOptions.size, visibleCount) {
        snapshotFlow { listState.layoutInfo.visibleItemsInfo.lastOrNull()?.index ?: -1 }.collect { lastIndex ->
            if (expanded && lastIndex >= (visibleCount.coerceAtMost(filteredOptions.size) - 1) && visibleCount < filteredOptions.size) visibleCount = (visibleCount + 5).coerceAtMost(filteredOptions.size)
        }
    }
    AppDialogFieldColumn(label = label, colors = AppDialogDefaultColors, modifier = modifier) {
        Box {
            Row(Modifier.fillMaxWidth().height(40.dp).border(1.dp, if (expanded) AppPrimary else AppBorder, RoundedCornerShape(6.dp)).clickable { expanded = !expanded }.onGloballyPositioned { anchorWidthPx = it.size.width }.padding(horizontal = 10.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Box(Modifier.weight(1f).horizontalScroll(chipScrollState)) {
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        values.forEach { value ->
                            Box(Modifier.background(Color(0xFFF0F6FF), RoundedCornerShape(6.dp)).padding(horizontal = 10.dp, vertical = 7.dp)) { Text("$value  ×", color = AppPrimary, fontSize = 12.sp, fontWeight = FontWeight.SemiBold, maxLines = 1, softWrap = false) }
                        }
                    }
                }
                Icon(painterResource(Res.drawable.chevron_down), contentDescription = "Select", tint = if (expanded) AppPrimary else AppNavy, modifier = Modifier.size(18.dp))
            }
            if (expanded) Popup(
                popupPositionProvider = object : PopupPositionProvider {
                    override fun calculatePosition(anchorBounds: IntRect, windowSize: IntSize, layoutDirection: LayoutDirection, popupContentSize: IntSize): IntOffset = IntOffset(anchorBounds.left, anchorBounds.bottom)
                },
                onDismissRequest = { expanded = false; query = "" },
                properties = PopupProperties(focusable = true, dismissOnBackPress = true, dismissOnClickOutside = true)
            ) {
                Surface(modifier = Modifier.width(anchorWidth.takeIf { it > 0.dp } ?: 320.dp), color = Color.White, shape = RoundedCornerShape(10.dp), border = BorderStroke(1.dp, AppBorder), shadowElevation = 14.dp) {
                    Column {
                        Column(Modifier.padding(10.dp)) {
                            Box(Modifier.fillMaxWidth().height(38.dp).border(1.dp, AppBorder, RoundedCornerShape(6.dp)).padding(horizontal = 10.dp), contentAlignment = Alignment.CenterStart) {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Icon(painterResource(Res.drawable.search), null, tint = AppMuted, modifier = Modifier.size(17.dp))
                                    BasicTextField(query, { query = it }, singleLine = true, textStyle = androidx.compose.ui.text.TextStyle(color = AppNavy, fontSize = 13.sp), cursorBrush = SolidColor(AppPrimary), modifier = Modifier.padding(start = 8.dp).fillMaxWidth(), decorationBox = { inner -> if (query.isBlank()) Text("Search $label", color = AppMuted, fontSize = 13.sp); inner() })
                                }
                            }
                        }
                        HorizontalDivider(color = AppBorder)
                        LazyColumn(state = listState, modifier = Modifier.fillMaxWidth().height(260.dp)) {
                            items(filteredOptions.take(visibleCount), key = { it }) { option ->
                                val selected = option in values
                                DropdownMenuItem(text = { Text(option, color = AppNavy, fontSize = 13.sp, fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Normal) }, onClick = { onValuesChanged(if (selected) values - option else values + option) })
                            }
                            if (filteredOptions.isEmpty()) item { Text("No matches found", color = AppMuted, fontSize = 13.sp, modifier = Modifier.padding(14.dp)) }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun NoticeAttachmentChip(attachment: NoticeAttachmentUi, modifier: Modifier, onRemove: (NoticeAttachmentUi) -> Unit) {
    val preview = attachment.previewBytes?.let(::decodeImageBitmap)
    Row(modifier.height(72.dp).border(1.dp, AppBorder, RoundedCornerShape(8.dp)).padding(horizontal = 10.dp), verticalAlignment = Alignment.CenterVertically) {
        if (preview != null) {
            Image(bitmap = preview, contentDescription = attachment.name, modifier = Modifier.size(52.dp), contentScale = ContentScale.Fit)
        } else {
            val tint = if (attachment.type == "PDF") Color(0xFFFFECEC) else Color(0xFFEAF8EF)
            Box(Modifier.size(36.dp).background(tint, RoundedCornerShape(6.dp)), contentAlignment = Alignment.Center) {
                Icon(painterResource(Res.drawable.document), contentDescription = null, tint = AppPrimary, modifier = Modifier.size(19.dp))
            }
        }
        Spacer(Modifier.width(8.dp))
        Column(Modifier.weight(1f)) { Text(attachment.name, color = AppNavy, fontSize = 12.sp, maxLines = 1); Text("${attachment.type}  •  ${attachment.sizeLabel}", color = AppMuted, fontSize = 11.sp) }
        Icon(painterResource(Res.drawable.close_x), contentDescription = "Remove ${attachment.name}", tint = AppNavy, modifier = Modifier.size(18.dp).clickable { onRemove(attachment) })
    }
}

private fun formatAttachmentSize(bytes: Long?): String {
    if (bytes == null || bytes <= 0L) return "Size unavailable"
    return if (bytes >= 1024L * 1024L) "${bytes / (1024L * 1024L)} MB" else "${(bytes / 1024L).coerceAtLeast(1L)} KB"
}

@Composable
private fun NoticeDialogFooter(onDismiss: () -> Unit, enabled: Boolean, allowSaveAsDraft: Boolean, onPreview: () -> Unit, onSave: (Boolean) -> Unit) {
    AppDialogFooter(
        actions = buildList {
            add(AppDialogFooterAction("Cancel", onDismiss, enabled = enabled, width = 120.dp))
            add(AppDialogFooterAction("Save & Publish", { onSave(true) }, AppDialogActionStyle.Primary, enabled, 170.dp))
            if (allowSaveAsDraft) {
                add(1, AppDialogFooterAction("Save as Draft", { onSave(false) }, enabled = enabled, width = 160.dp))
            }
        },
        colors = AppDialogDefaultColors,
        footerHeight = 78.dp,
        leadingContent = {
            AppDialogFooterButton(
                action = AppDialogFooterAction("Preview", onPreview, enabled = enabled, width = 150.dp),
                colors = AppDialogDefaultColors,
                modifier = Modifier.width(150.dp)
            )
        }
    )
}

private fun noticeContentType(filename: String): String = when (filename.substringAfterLast('.', "").lowercase()) {
    "pdf" -> "application/pdf"
    "docx" -> "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    "jpg", "jpeg" -> "image/jpeg"
    "png" -> "image/png"
    else -> "application/octet-stream"
}

@Composable
private fun NoticeTitle(value: String, error: String = "", onChange: (String) -> Unit) {
    AppDialogFieldColumn(label = "Notice Title", required = true, error = error, colors = AppDialogDefaultColors) {
        AppDialogTextField(value, onChange, "Enter notice title", colors = AppDialogDefaultColors, maxLength = 200, error = error)
    }
}

@Composable
private fun NoticeRichContent(value: String, error: String = "", onChange: (String) -> Unit) {
    AppDialogFieldColumn(label = "Notice Content", required = true, error = error, colors = AppDialogDefaultColors) {
        Box {
            AppDialogTextField(value, onChange, "Enter notice content here...", colors = AppDialogDefaultColors, singleLine = false, fieldHeight = 190.dp, fontSize = 14.sp, maxLength = 2000, error = error)
            Text("${value.length} / 2000 Characters", color = AppMuted, fontSize = 11.sp, modifier = Modifier.align(Alignment.BottomEnd).padding(horizontal = 12.dp, vertical = 9.dp))
        }
    }
}
